import { getAtlasApiBaseUrl } from '../../../services/getAtlasApiBaseUrl';
import { atlasFetch } from '../../../services/api/atlasBackendClient';

type TimeResult = { index: number; time: number };

type GoogleTransitRequest = {
  origin: [number, number];
  destination: [number, number];
};

type GoogleTransitBatchResponse = {
  results: Array<{ duration_minutes: number | null }>;
};

const GOOGLE_MAPS_API_BASE_URL = `${getAtlasApiBaseUrl()}/api/google-maps`;

async function fetchTransitTravelTimes(
  requests: GoogleTransitRequest[],
  arrivalTime: Date
): Promise<number[]> {
  if (requests.length === 0) return [];

  const response = await atlasFetch(`${GOOGLE_MAPS_API_BASE_URL}/transit-times`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      requests,
      arrival_time: arrivalTime.toISOString(),
    }),
  });

  if (!response.ok) {
    throw new Error(`Google transit proxy error: ${response.status}`);
  }

  const data = (await response.json()) as GoogleTransitBatchResponse;
  return data.results.map((result) => result.duration_minutes ?? 999);
}

export async function callGoogleRoutingAPI(
  origin: [number, number],
  destination: [number, number],
  arrivalTime: Date
): Promise<number> {
  const [duration] = await fetchTransitTravelTimes([{ origin, destination }], arrivalTime);
  return duration ?? 999;
}

export async function batchCallGoogleRoutingAPI(
  batchRequests: GoogleTransitRequest[],
  arrivalTime: Date,
  transitTimeCache: Map<string, number>,
  batchSize: number = 10
): Promise<number[]> {
  if (batchRequests.length === 0) return [];

  const adaptiveBatchSize = batchRequests.length > 100
    ? Math.min(batchSize, 15)
    : Math.min(batchSize, 8);

  const results: number[] = new Array(batchRequests.length).fill(999);
  const uncachedRequests: Array<{ index: number; request: GoogleTransitRequest; cacheKey: string }> = [];

  batchRequests.forEach((request, index) => {
    const cacheKey = `${request.origin[0]},${request.origin[1]}-${request.destination[0]},${request.destination[1]}-${arrivalTime.getTime()}`;
    const cached = transitTimeCache.get(cacheKey);
    if (cached != null) {
      results[index] = cached;
      return;
    }
    uncachedRequests.push({ index, request, cacheKey });
  });

  for (let i = 0; i < uncachedRequests.length; i += adaptiveBatchSize) {
    const batch = uncachedRequests.slice(i, i + adaptiveBatchSize);
    const durations = await fetchTransitTravelTimes(
      batch.map((entry) => entry.request),
      arrivalTime
    );

    batch.forEach((entry, batchIndex) => {
      const duration = durations[batchIndex] ?? 999;
      results[entry.index] = duration;
      if (duration !== 999) {
        transitTimeCache.set(entry.cacheKey, duration);
      }
    });
  }

  return results;
}
